import CheckIcon from '@mui/icons-material/Check'
import {Box, Typography, List, ListItem, ListItemButton, ListItemText} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {getUnloadingMethodText} from './unloadingMethodUtils'

interface UnloadingMethodListProps {
  selectedUnloadingMethod?: string
  onUnloadingMethodSelect: (code: string) => void
  availableUnloadingMethods: string[]
  pumpEnabled?: boolean
  'data-test-id'?: string
}

/**
 * Component that displays a list of unloading method options for selection
 * Styled to match the UsageList component for consistency
 */
export const UnloadingMethodList: React.FC<UnloadingMethodListProps> = ({
  selectedUnloadingMethod,
  onUnloadingMethodSelect,
  availableUnloadingMethods,
  pumpEnabled = false,
  'data-test-id': dataTestId = 'unloading-method-list'
}) => {
  const {t} = useTranslation()

  // Process and sort unloading methods following React Native pattern
  const sortedUnloadingMethods = React.useMemo(() => {
    return availableUnloadingMethods
      .map((code) => ({
        code,
        text: getUnloadingMethodText(t, code)
      }))
      .filter((unloadingMethod) => {
        // Filter out 'O' option when pump is enabled
        return !pumpEnabled || (pumpEnabled && unloadingMethod.code !== 'O')
      })
  }, [availableUnloadingMethods, pumpEnabled, t])

  return (
    <Box sx={{width: '100%'}}>
      <List
        sx={{
          padding: 0,
          width: '100%'
        }}
        data-test-id={dataTestId}
      >
        {sortedUnloadingMethods.length > 0 ? (
          sortedUnloadingMethods.map((unloadingMethod, index) => {
            const {code, text} = unloadingMethod
            const isSelected = selectedUnloadingMethod === code

            return (
              <ListItem
                key={code}
                sx={{
                  padding: 0,
                  width: '100%'
                }}
              >
                <ListItemButton
                  onClick={() => onUnloadingMethodSelect(code)}
                  selected={isSelected}
                  sx={{
                    backgroundColor: isSelected ? '#f2f8fd' : '#ffffff',
                    borderBottom:
                      index === sortedUnloadingMethods.length - 1 ? 'none' : '1px solid #e8ecf0',
                    padding: '12px 16px',
                    minHeight: 'auto',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#f2f8fd'
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#f2f8fd',
                      '&:hover': {
                        backgroundColor: '#f2f8fd'
                      }
                    }
                  }}
                  data-test-id={`${dataTestId}-option-${code}`}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#29333d',
                          textAlign: 'left'
                        }}
                      >
                        {text}
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      flex: 1
                    }}
                  />
                  {selectedUnloadingMethod === code && (
                    <CheckIcon
                      sx={{
                        color: '#016ad4',
                        fontSize: '20px',
                        marginLeft: '16px'
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            )
          })
        ) : (
          <Box sx={{p: 3, textAlign: 'center'}}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#52667a'
              }}
            >
              {t(
                'orderIntake.rmc.steps.unloading.noOptionsAvailable',
                'No unloading method options are currently available.'
              )}
            </Typography>
          </Box>
        )}
      </List>
    </Box>
  )
}
